/* 
  File: C:\Users\Markus Diehl\source\Expert4Germany\src\pages\stb\StbJobMarketplace.js
  Name: StbJobMarketplace.js
  Zweck: Diese Komponente implementiert eine vollwertige Stellenbörse, 
         angelehnt an LinkedIn, und richtet sich an Fachkräfte, um 
         aktiv nach passenden Jobangeboten zu suchen.
*/

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import stb_config from "../../config/config"; // Pfad: ../../config/config.js (enthält bspw. API_BASE_URL)

const StbJobMarketplace = () => {
  // State zur Speicherung der aktiven Stellenanzeigen (Job Postings)
  const [stb_jobPostings, setStb_jobPostings] = useState([]);
  // State für Suchbegriff
  const [stb_searchTerm, setStb_searchTerm] = useState("");
  // State für den ausgewählten Region-Filter (z. B. Land)
  const [stb_selectedRegion, setStb_selectedRegion] = useState("all");
  // States für Lade- und Fehlermeldungen
  const [stb_loading, setStb_loading] = useState(true);
  const [stb_error, setStb_error] = useState("");
  const [selectedPosting, setSelectedPosting] = useState(null);

  // Funktion: Abrufen der aktiven Job Postings vom Server
  const stb_fetchJobPostings = async () => {
    try {
      const stb_response = await axios.get(`${stb_config.API_BASE_URL}/api/stb/ads/public`, {
        params: { page: 1, limit: 60 },
      });
      setStb_jobPostings(Array.isArray(stb_response.data?.rows) ? stb_response.data.rows : []);
      setStb_error("");
    } catch (stb_err) {
      console.error("stb_Error fetching job postings:", stb_err);
      setStb_error("Fehler beim Laden der Suchanzeigen");
    } finally {
      setStb_loading(false);
    }
  };

  // Effekt: Beim ersten Laden der Komponente die Job Postings abrufen
  useEffect(() => {
    stb_fetchJobPostings();
  }, []);

  // Filter: Suche und Region
  const stb_filteredPostings = useMemo(() => {
    const search = stb_searchTerm.trim().toLowerCase();
    return stb_jobPostings.filter((stb_posting) => {
      const titel = String(stb_posting.titel || "").toLowerCase();
      const beschreibung = String(stb_posting.beschreibung || "").toLowerCase();
      const beruf = String(stb_posting.beruf_text || "").toLowerCase();
      const ort = String(stb_posting.standort_ort || "").toLowerCase();
      const land = String(stb_posting.standort_land || "").toUpperCase();

      const stb_matchesSearch =
        !search ||
        titel.includes(search) ||
        beschreibung.includes(search) ||
        beruf.includes(search) ||
        ort.includes(search);
      const stb_matchesRegion = stb_selectedRegion === "all" || land === stb_selectedRegion;
      return stb_matchesSearch && stb_matchesRegion;
    });
  }, [stb_jobPostings, stb_searchTerm, stb_selectedRegion]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Kopfzeile */}
      <header className="bg-white shadow p-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Stellenbörse</h1>
            <p className="mt-2 text-gray-600">
              Finde Deinen nächsten Karriereschritt in Europa – maßgeschneidert für internationale Fachkräfte.
            </p>
          </div>
          {/* Suchfeld und Region-Filter */}
          <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4">
            <input
              type="text"
              placeholder="Jobtitel, Stichworte..."
              value={stb_searchTerm}
              onChange={(e) => setStb_searchTerm(e.target.value)}
              className="w-full md:w-64 p-3 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={stb_selectedRegion}
              onChange={(e) => setStb_selectedRegion(e.target.value)}
              className="w-full md:w-48 p-3 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle Regionen</option>
              <option value="DE">Deutschland</option>
              <option value="AT">Österreich</option>
              <option value="CH">Schweiz</option>
              {/* Weitere Länder können hier hinzugefügt werden */}
            </select>
          </div>
        </div>
      </header>

      {/* Hauptbereich */}
      <main className="max-w-6xl mx-auto p-6">
        {stb_loading ? (
          <div className="text-center text-gray-700">Lädt...</div>
        ) : stb_error ? (
          <div className="text-center text-red-500">{stb_error}</div>
        ) : stb_filteredPostings.length === 0 ? (
          <div className="text-center text-gray-700">Keine Suchanzeigen gefunden.</div>
        ) : (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {stb_filteredPostings.map((stb_posting) => (
              <div
                key={stb_posting.id}
                className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition duration-200"
              >
                <h2 className="text-2xl font-semibold text-gray-800">{stb_posting.titel}</h2>
                <p className="mt-2 text-gray-600">{stb_posting.beschreibung}</p>
                {stb_posting.skills && (
                  <p className="mt-4 text-sm text-gray-500">
                    <strong>Skills:</strong> {stb_posting.skills}
                  </p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  <strong>Region:</strong> {[stb_posting.standort_land, stb_posting.standort_ort].filter(Boolean).join(" / ") || "Global"}
                </p>
                {/* Call-to-Action-Button */}
                <button
                  className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
                  onClick={() => setSelectedPosting(stb_posting)}
                >
                  Mehr erfahren
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {selectedPosting ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-blue-700">{selectedPosting.beruf_text || "-"}</p>
                <h2 className="text-2xl font-bold text-gray-900">{selectedPosting.titel}</h2>
                <p className="mt-1 text-sm text-gray-600">
                  {[selectedPosting.standort_land, selectedPosting.standort_ort].filter(Boolean).join(" / ") || "Standort offen"}
                  {" · "}
                  {selectedPosting.arbeitsform || "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPosting(null)}
                className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
              >
                Schliessen
              </button>
            </div>

            <div className="mt-5 space-y-4 text-gray-800">
              <section>
                <h3 className="font-semibold text-gray-900">Beschreibung</h3>
                <p className="mt-1 whitespace-pre-wrap">{selectedPosting.beschreibung || "-"}</p>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900">Aufgaben</h3>
                <p className="mt-1 whitespace-pre-wrap">{selectedPosting.aufgaben || "-"}</p>
              </section>
              <section>
                <h3 className="font-semibold text-gray-900">Anforderungen</h3>
                <p className="mt-1 whitespace-pre-wrap">{selectedPosting.anforderungen || "-"}</p>
              </section>
              <section className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Onboarding Bonus</p>
                  <p className="text-lg font-semibold">{selectedPosting.onboarding_bonus ?? "-"} EUR</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Wohnungsunterstuetzung</p>
                  <p className="text-lg font-semibold">{Number(selectedPosting.wohnung) === 1 ? "Ja" : "Nein"}</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {/* Footer */}
      <footer className="bg-white border-t mt-10 py-4">
        <div className="max-w-6xl mx-auto text-center text-gray-600">
          © {new Date().getFullYear()} Experts4Germany. Alle Rechte vorbehalten.
        </div>
      </footer>
    </div>
  );
};

export default StbJobMarketplace;
